import React, { useState, useEffect } from 'react';

/**
 * Unified Error / Engagement Modal — adapted from the dhi digital-audit product
 * so both audit tools share one error-state design system.
 *
 * Types (drive which config block is used):
 *   URL_UNREACHABLE  — DNS, timeout, 4xx/5xx on URL fetch
 *   NOT_HOTEL        — URL is reachable but isn't a hotel (OTA, consultant site,
 *                       JS-only, weak keywords, hardcoded non-hotel brand)
 *   CHAIN_BRAND      — URL is a hotel, just a global chain we don't audit
 *   NON_HOTEL_GBP    — GBP found but category isn't hotel-like (actionable:
 *                       user goes to business.google.com to fix category)
 *   AUDIT_FAILED     — pipeline crashed or timed out
 *   SOFT_ENGAGEMENT  — 3+ audits from same email (Calendly CTA, not an error)
 */

const CALENDLY_URL =
  'https://calendly.com/dhihospitality-audit-review/30min' +
  '?hide_event_type_details=1&hide_gdpr_banner=1';

const CALENDLY_FALLBACK_EMAIL = 'hello@dhihospitality.com';

// If the Calendly iframe doesn't signal `load` within this window we assume
// the embed is blocked (corp firewalls, ad blockers, calendly.com outage) and
// show a fallback "Email us instead" panel. Without this, the modal silently
// rendered an empty white box — exactly when the user wanted to convert.
const CALENDLY_LOAD_TIMEOUT_MS = 8000;

const MODAL_CONFIG = {
  URL_UNREACHABLE: {
    title: "We couldn't reach this website",
    getDescription: (ctx) => (
      <>
        The URL <strong>{ctx.url || 'you entered'}</strong> returned{' '}
        {ctx.statusCode ? `a ${ctx.statusCode} error` : 'no response'}.
        This could be a temporary issue, or the site may be down.
      </>
    ),
    infoText: "Double-check the URL for typos, or try again in a few minutes. Make sure the website is publicly accessible.",
    primaryBtn: { text: "Try Again", action: "retry", icon: "refresh" },
    secondaryBtn: { text: "Edit URL", action: "editUrl", icon: "edit" },
  },

  NOT_HOTEL: {
    title: "This doesn't look like a hotel website",
    getDescription: () => "Please enter a hotel's website or Google Business Profile link.",
    infoText: null,
    primaryBtn: { text: "Enter a Different URL", action: "editUrl", icon: "edit" },
    secondaryBtn: null,
  },

  CHAIN_BRAND: {
    title: "This is a global hotel chain website",
    getDescription: () => "Our audit is designed for independent and regional brands where the property controls its digital strategy.",
    infoText: null,
    primaryBtn: { text: "Enter a Different URL", action: "editUrl", icon: "edit" },
    secondaryBtn: null,
  },

  NON_HOTEL_GBP: {
    title: "Your Google Business Profile isn't set up as a hotel",
    getDescription: (ctx) => ctx.description || (
      "We couldn't find a hotel-category GBP for this property. Verify your listing at business.google.com, make sure the primary category is Hotel / Resort / similar, then try again."
    ),
    infoText: null,
    primaryBtn: { text: "Enter a Different URL", action: "editUrl", icon: "edit" },
    secondaryBtn: null,
  },

  AUDIT_FAILED: {
    title: "The audit couldn't complete",
    getDescription: (ctx) => (
      <>
        We ran into an issue while analysing <strong>{ctx.hotelName || 'your hotel'}</strong>.
        This is typically a temporary problem with one of our data providers.
      </>
    ),
    infoText: "Running the audit again usually resolves this. If it persists, try editing the hotel name to match your Google listing exactly.",
    primaryBtn: { text: "Retry Audit", action: "retry", icon: "refresh" },
    secondaryBtn: { text: "Start Over", action: "startOver", icon: "home" },
  },

  SOFT_ENGAGEMENT: {
    title: "Getting serious about direct bookings?",
    getDescription: (ctx) => (
      <>
        You've run <strong>{ctx.auditCount || '3+'} audits</strong> — looks like you're committed to improving
        your direct channel. Let's talk about how we can help you implement these recommendations.
      </>
    ),
    infoText: null,
    primaryBtn: { text: "Book a Free Consultation", action: "bookConsultation", icon: "calendar" },
    secondaryBtn: { text: "Continue Auditing", action: "dismiss", icon: "arrow" },
  },
};

const Icons = {
  refresh: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M23 4v6h-6M1 20v-6h6"/>
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
    </svg>
  ),
  edit: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  home: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  ),
  calendar: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  arrow: (color) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  alert: (color) => (
    <svg viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="2.5" opacity="0.15"/>
      <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="2.5" strokeDasharray="138.2" strokeDashoffset="34.5" strokeLinecap="round"/>
      <path d="M24 16v10" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="24" cy="32" r="1.5" fill={color}/>
    </svg>
  ),
  check: (color) => (
    <svg viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="2.5" opacity="0.15"/>
      <circle cx="24" cy="24" r="22" stroke={color} strokeWidth="2.5" strokeDasharray="138.2" strokeDashoffset="0" strokeLinecap="round"/>
      <path d="M16 24l6 6 10-12" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

const ACCENT_BG = 'rgba(45, 77, 224, 0.08)';
const ACCENT_FG = '#2D4DE0';
const ACCENT_STRIP = 'linear-gradient(90deg, #2D4DE0 0%, #5B7FE8 50%, #8FA3ED 100%)';

export default function ErrorModal({
  isOpen,
  type = 'AUDIT_FAILED',
  context = {},
  onRetry,
  onEditUrl,
  onStartOver,
  onDismiss,
  onBookConsultation,
  onClose,
}) {
  const [showCalendly, setShowCalendly] = useState(false);
  const [calendlyFailed, setCalendlyFailed] = useState(false);

  // Calendly load watchdog — flip to fallback if the iframe hasn't reported
  // `load` within CALENDLY_LOAD_TIMEOUT_MS. Re-runs whenever the iframe is
  // mounted (initial open OR after a Retry click), so a transient block on
  // one open doesn't permanently lock the user out of trying again.
  useEffect(() => {
    if (!showCalendly) {
      // Modal closed — reset so a fresh open starts with a fresh watchdog.
      if (calendlyFailed) setCalendlyFailed(false);
      return;
    }
    if (calendlyFailed) return;
    const t = setTimeout(() => setCalendlyFailed(true), CALENDLY_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [showCalendly, calendlyFailed]);

  // GTM — fire once per modal open. SOFT_ENGAGEMENT is a positive prompt (not an
  // error) and has its own CTA event, so skip it here to keep error segmentation clean.
  useEffect(() => {
    if (!isOpen || type === 'SOFT_ENGAGEMENT') return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'error_modal_shown', error_type: type });
    } catch (_) {}
  }, [isOpen, type]);

  if (!isOpen) return null;

  const config = MODAL_CONFIG[type] || MODAL_CONFIG.AUDIT_FAILED;
  const isEngagement = type === 'SOFT_ENGAGEMENT';

  const handleAction = (action) => {
    switch (action) {
      case 'retry':            onRetry?.(); break;
      case 'editUrl':          onEditUrl?.(); break;
      case 'startOver':        onStartOver?.(); break;
      case 'dismiss':          setShowCalendly(false); onDismiss?.(); break;
      case 'bookConsultation': setShowCalendly(true); onBookConsultation?.(); break;
      default:                 setShowCalendly(false); onClose?.();
    }
  };

  const handleClose = () => { setShowCalendly(false); onClose?.(); };

  // Resolve description (fn or string) + infoText override from context
  const description = typeof config.getDescription === 'function'
    ? config.getDescription(context)
    : (config.description || '');
  const infoText = context.infoText ?? config.infoText;
  const title = context.title || config.title;

  return (
    <div
      className="error-modal-backdrop"
      onClick={isEngagement && !showCalendly ? (onDismiss || onClose) : undefined}
      data-testid="error-modal-backdrop"
    >
      <div
        className={`error-modal ${showCalendly ? 'error-modal-calendly' : ''}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`error-modal-${type.toLowerCase()}`}
      >
        <div className="error-modal-accent" style={{ background: ACCENT_STRIP }} />

        {showCalendly ? (
          <>
            <div className="error-modal-calendly-header">
              <h3 className="error-modal-calendly-title">Book Your Free Consultation</h3>
              <button className="error-modal-calendly-close" onClick={handleClose} data-testid="calendly-close-btn" aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="error-modal-calendly-container">
              {calendlyFailed ? (
                <div
                  className="error-modal-calendly-fallback"
                  data-testid="calendly-fallback"
                  style={{
                    display:'flex', flexDirection:'column', alignItems:'center',
                    justifyContent:'center', padding:'32px 28px', textAlign:'center',
                    gap:14, fontFamily:"'DM Sans',sans-serif",
                  }}
                >
                  <div style={{fontFamily:"'Outfit',sans-serif", fontSize:17, fontWeight:700, color:'#0A0A1A'}}>
                    Calendar didn't load
                  </div>
                  <div style={{fontSize:13.5, lineHeight:1.6, color:'rgba(10,10,26,0.65)', maxWidth:360}}>
                    Calendly may be blocked on your network or temporarily unreachable.
                    Email us and we'll book the 30-minute strategy session for you.
                  </div>
                  <a
                    href={`mailto:${CALENDLY_FALLBACK_EMAIL}?subject=${encodeURIComponent('Book a strategy session')}`}
                    data-testid="calendly-fallback-email"
                    style={{
                      marginTop:6, padding:'12px 20px', borderRadius:10, background:'#2D4DE0',
                      color:'#fff', fontWeight:700, fontSize:14, textDecoration:'none',
                      boxShadow:'0 8px 24px rgba(45,77,224,0.28)',
                    }}
                  >
                    Email {CALENDLY_FALLBACK_EMAIL}
                  </a>
                  <button
                    onClick={() => { setCalendlyFailed(false); }}
                    data-testid="calendly-retry-btn"
                    style={{
                      marginTop:2, background:'transparent', border:'none',
                      color:'#2D4DE0', fontSize:12.5, fontWeight:600,
                      cursor:'pointer', textDecoration:'underline',
                    }}
                  >
                    Try loading Calendly again
                  </button>
                </div>
              ) : (
                <iframe
                  src={CALENDLY_URL}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title="Book a consultation"
                  data-testid="calendly-iframe"
                  onLoad={() => setCalendlyFailed(false)}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="error-modal-header">
              <div className="error-modal-icon" style={{ '--icon-bg': ACCENT_BG }}>
                {isEngagement ? Icons.check(ACCENT_FG) : Icons.alert(ACCENT_FG)}
              </div>
            </div>

            <div className="error-modal-body">
              <div className="error-modal-title">{title}</div>
              <div className="error-modal-desc">{description}</div>

              {infoText && (
                <div className="error-modal-info-card">
                  <div className="error-modal-info-row">
                    <svg className="error-modal-info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M12 8v4M12 16h.01"/>
                    </svg>
                    <div className="error-modal-info-text">{infoText}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="error-modal-actions">
              {config.primaryBtn && (
                <button
                  className="error-modal-btn error-modal-btn-primary"
                  onClick={() => handleAction(config.primaryBtn.action)}
                  data-testid="error-modal-primary-btn"
                >
                  {Icons[config.primaryBtn.icon]?.('#FFFFFF')}
                  {context.buttonText || config.primaryBtn.text}
                </button>
              )}
              {config.secondaryBtn && (
                <button
                  className="error-modal-btn error-modal-btn-outline"
                  onClick={() => handleAction(config.secondaryBtn.action)}
                  data-testid="error-modal-secondary-btn"
                >
                  {Icons[config.secondaryBtn.icon]?.('#1A1D26')}
                  {config.secondaryBtn.text}
                </button>
              )}
              {!isEngagement && (
                <>
                  <div className="error-modal-divider" />
                  <div className="error-modal-contact">
                    <a href="mailto:hello@dhihospitality.com">
                      Need help? <span>Get in touch</span>
                    </a>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`
        .error-modal-backdrop {
          position: fixed; inset: 0;
          background: rgba(26,29,38,0.5);
          backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000;
          animation: errorFadeIn 0.3s ease;
        }
        @keyframes errorFadeIn { from {opacity:0;} to {opacity:1;} }
        @keyframes errorModalIn {
          from { opacity:0; transform: scale(0.96) translateY(12px); }
          to   { opacity:1; transform: scale(1) translateY(0); }
        }
        .error-modal {
          width: 440px; max-width: 92vw;
          background: #FFFFFF; border-radius: 20px; overflow: hidden;
          animation: errorModalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 0 0 1px rgba(0,0,0,0.04),0 8px 40px rgba(0,0,0,0.12),0 24px 80px rgba(0,0,0,0.08);
        }
        .error-modal.error-modal-calendly { width: 480px; max-width: 95vw; height: 680px; max-height: 90vh; }
        .error-modal-calendly-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 20px; border-bottom: 1px solid #E2E4E9;
        }
        .error-modal-calendly-title {
          font-family: 'Outfit', sans-serif;
          font-size: 16px; font-weight: 600; color: #1A1D26; margin: 0;
        }
        .error-modal-calendly-close {
          width: 32px; height: 32px; border: none; background: #F8F9FB;
          border-radius: 8px; cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease;
        }
        .error-modal-calendly-close:hover { background: #E2E4E9; }
        .error-modal-calendly-close svg { width: 18px; height: 18px; color: #6B7280; }
        .error-modal-calendly-container { flex: 1; height: calc(100% - 60px); min-height: 580px; }
        .error-modal-calendly-container iframe { border: none; }

        .error-modal-accent { height: 3px; }

        .error-modal-header {
          padding: 36px 36px 0;
          display: flex; flex-direction: column; align-items: center;
        }
        .error-modal-icon { width: 48px; height: 48px; position: relative; }
        .error-modal-icon::before {
          content: ''; position: absolute; inset: -8px;
          border-radius: 50%; background: var(--icon-bg, rgba(45,77,224,0.08));
        }
        .error-modal-icon svg { width: 48px; height: 48px; position: relative; z-index: 1; }

        .error-modal-body { padding: 24px 36px 0; text-align: center; }
        .error-modal-title {
          font-family: 'Outfit', sans-serif;
          font-size: 20px; font-weight: 600; color: #1A1D26;
          letter-spacing: -0.01em; margin-bottom: 10px;
        }
        .error-modal-desc { font-size: 14px; line-height: 1.65; color: #6B7280; margin-bottom: 24px; }
        .error-modal-desc strong { color: #1A1D26; font-weight: 600; }

        .error-modal-info-card {
          background: #F5F7FF; border: 1px solid #D6DEFF; border-radius: 12px;
          padding: 16px 20px; text-align: left; margin-bottom: 28px;
        }
        .error-modal-info-row { display: flex; align-items: flex-start; gap: 12px; }
        .error-modal-info-icon { width: 18px; height: 18px; color: #2D4DE0; flex-shrink: 0; margin-top: 1px; }
        .error-modal-info-text { font-size: 13px; line-height: 1.55; color: #4B5563; }

        .error-modal-actions { padding: 0 36px 32px; display: flex; flex-direction: column; gap: 8px; }
        .error-modal-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 13px 20px; border-radius: 12px;
          font-family: 'DM Sans', sans-serif; font-size: 14px; font-weight: 600;
          cursor: pointer; border: none; transition: all 0.2s ease; letter-spacing: 0.005em;
        }
        .error-modal-btn svg { width: 16px; height: 16px; }
        .error-modal-btn-primary {
          background: #2D4DE0; color: #FFFFFF;
          box-shadow: 0 1px 3px rgba(45,77,224,0.3);
        }
        .error-modal-btn-primary:hover {
          background: #2341C0; box-shadow: 0 2px 8px rgba(45,77,224,0.4); transform: translateY(-1px);
        }
        .error-modal-btn-outline {
          background: #FFFFFF; color: #1A1D26; border: 1px solid #E2E4E9;
        }
        .error-modal-btn-outline:hover { background: #F8F9FB; border-color: #D1D5DB; }

        .error-modal-divider { height: 1px; background: #F0F1F3; margin: 4px 0; }
        .error-modal-contact { text-align: center; padding: 4px 0; }
        .error-modal-contact a {
          font-size: 13px; color: #9CA3AF; text-decoration: none; transition: color 0.15s;
        }
        .error-modal-contact a:hover { color: #2D4DE0; }
        .error-modal-contact a span { text-decoration: underline; text-underline-offset: 2px; }
      `}</style>
    </div>
  );
}

/**
 * Map our backend error_type to ErrorModal.type.
 * Kept out of the component so the logic lives next to call sites that care.
 */
export function errorTypeToModalType(errorType, fallback = 'AUDIT_FAILED') {
  const t = (errorType || '').toLowerCase();
  if (t === 'chain_brand') return 'CHAIN_BRAND';
  if (t === 'non_hotel_gbp' || t === 'no_hotel_gbp') return 'NON_HOTEL_GBP';
  if (['ota', 'non_hotel_name', 'content_unavailable', 'insufficient_keywords',
       'not_hotel', 'non_hotel_website'].includes(t)) return 'NOT_HOTEL';
  if (t === 'url_unreachable') return 'URL_UNREACHABLE';
  return fallback;
}
